/**
 * Dealers 列表页 —— SUPER_ADMIN 视角。
 * 路由 /dealers
 */
import {
  Button,
  Container,
  Group,
  Modal,
  NumberInput,
  Select,
  Stack,
  Tabs,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { IconBuildingStore, IconPlus, IconSearch, IconSettings } from '@tabler/icons-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import {
  createDealer,
  getDealerSystemSettings,
  searchUsersForDealer,
  updateDealerSystemSettings,
  type DealerCreateRequest,
  type SearchableUser,
} from '@mygpt/app/apis/services/dealer';

import { useGPTStore } from '@/store';

import { LookupPanel } from './components/LookupPanel';
import { OverviewPanel } from './components/OverviewPanel';

type TabKey = 'dealers' | 'lookup';

const hasConfig = (userInfo: any, key: string): boolean => {
  if (!userInfo?.userconfigs) return false;
  return userInfo.userconfigs.some((c: any) => c.key === key && String(c.value).toLowerCase() === 'true');
};

const DealersListPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { userInfo } = useGPTStore((state) => state);

  // 权限：列表页仅 SUPER_ADMIN 可见；DEALER_ADMIN 直接重定向到自助页
  useEffect(() => {
    if (!userInfo) return;
    const isSuperAdmin = hasConfig(userInfo, 'super_admin');
    const isDealerAdmin = hasConfig(userInfo, 'dealer_admin');
    if (!isSuperAdmin && isDealerAdmin) {
      navigate('/dealers/me', { replace: true });
    } else if (!isSuperAdmin) {
      // 非 SUPER_ADMIN 也非 DEALER_ADMIN → 没权限，回首页
      navigate('/', { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInfo]);
  // v0.9: 表/KPI 数据由 OverviewPanel 内部拉取，外部只传 search + refreshKey
  const [search, setSearch] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  const [opened, setOpened] = useState(false);

  // 创建表单
  const [form, setForm] = useState<Omit<DealerCreateRequest, 'initial_admin_new' | 'initial_admin_user_id'>>({
    code: '',
    name: '',
    contact_email: '',
    contact_phone: '',
    incentive_rate: '0.3000',
  });

  // 仅支持「选已有用户」模式
  const [userQuery, setUserQuery] = useState('');
  const [debouncedQuery] = useDebouncedValue(userQuery, 300);
  const [userOptions, setUserOptions] = useState<SearchableUser[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

  // 创建结果

  // 测试用全局配置 —— 生产环境完全隐藏入口，避免误把 trial 改成立刻扣费
  const isProd = import.meta.env.VITE_STAGE === 'production';
  const [settingsOpen, setSettingsOpen] = useState(false);

  // v0.9: Tabs —— ディーラー / コード検索（原列表已合并入 ディーラー Tab），
  // URL ?tab= 同步便于分享。旧 tab=overview/list 等值统一兜底到 dealers。
  const [sp, setSp] = useSearchParams();
  const tabParam = sp.get('tab');
  const activeTab: TabKey = tabParam === 'lookup' ? 'lookup' : 'dealers';
  const setActiveTab = (k: TabKey) => {
    sp.set('tab', k);
    setSp(sp, { replace: true });
  };
  const [trialOffsetSeconds, setTrialOffsetSeconds] = useState<number | ''>('');
  const [savingSettings, setSavingSettings] = useState(false);

  // 打开 settings modal 时读当前配置
  useEffect(() => {
    if (!settingsOpen) return;
    getDealerSystemSettings()
      .then((res) => {
        setTrialOffsetSeconds(res.data.trial_end_offset_seconds ?? '');
      })
      .catch(() => setTrialOffsetSeconds(''));
  }, [settingsOpen]);

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      const val = trialOffsetSeconds === '' ? null : Number(trialOffsetSeconds);
      await updateDealerSystemSettings({ trial_end_offset_seconds: val });
      const msg =
        val === null
          ? (t('dealer.trial_offset_cleared') as string)
          : val === 0
          ? (t('dealer.trial_offset_no_trial') as string)
          : `trial = now + ${val}s`;
      notifications.show({
        title: t('dealer.saved'),
        message: msg,
        color: 'green',
      });
      setSettingsOpen(false);
    } catch (e: any) {
      notifications.show({
        title: 'Error',
        message: e?.response?.data?.detail || String(e),
        color: 'red',
      });
    } finally {
      setSavingSettings(false);
    }
  };

  // Modal 打开 + 后续输入：拉用户列表（空 query 时后端返回最近 N 个）
  useEffect(() => {
    if (!opened) return;
    searchUsersForDealer({ q: debouncedQuery.trim() || undefined, limit: 20 })
      .then((res) => setUserOptions(res.data))
      .catch(() => setUserOptions([]));
  }, [opened, debouncedQuery]);

  const selectData = useMemo(
    () =>
      userOptions.map((u) => ({
        value: u.id,
        label: `${u.email} (${u.name})${u.dealer_id ? ' · ' + t('dealer.user_already_in_dealer') : ''}`,
        disabled: !!u.dealer_id,
      })),
    [userOptions, t]
  );

  const resetForm = () => {
    setForm({ code: '', name: '', contact_email: '', contact_phone: '', incentive_rate: '0.3000' });
    setUserQuery('');
    setSelectedUserId(null);
    setUserOptions([]);
  };

  const submit = async () => {
    if (!selectedUserId) {
      notifications.show({
        title: 'Error',
        message: t('dealer.select_user_required') as string,
        color: 'red',
      });
      return;
    }
    const payload: DealerCreateRequest = {
      ...form,
      initial_admin_user_id: selectedUserId,
    };
    try {
      const res = await createDealer(payload);
      notifications.show({
        title: t('dealer.create'),
        message: `${res.data.dealer.code} / ${res.data.dealer.name}`,
        color: 'green',
      });
      setOpened(false);
      resetForm();
      setRefreshKey((k) => k + 1);
    } catch (e: any) {
      notifications.show({
        title: 'Error',
        message: e?.response?.data?.detail || String(e),
        color: 'red',
      });
    }
  };

  return (
    <Container size="xl" py="md">
      <Group justify="space-between" mb="md">
        <Title order={2}>{t('dealer.menu', 'ディーラー管理')}</Title>
        <Group gap="xs">
          {!isProd && (
            <Button variant="light" leftSection={<IconSettings size={16} />} onClick={() => setSettingsOpen(true)}>
              {t('dealer.test_settings')}
            </Button>
          )}
        </Group>
      </Group>

      <Tabs value={activeTab} onChange={(v) => v && setActiveTab(v as TabKey)} mb="md">
        <Tabs.List>
          <Tabs.Tab value="dealers" leftSection={<IconBuildingStore size={14} />}>
            {t('dealer.tab_dealers')}
          </Tabs.Tab>
          <Tabs.Tab value="lookup" leftSection={<IconSearch size={14} />}>
            {t('dealer.tab_lookup')}
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="dealers" pt="md">
          <Group justify="space-between" mb="md">
            <TextInput
              placeholder={`${t('dealer.code')} / ${t('dealer.name')} / ${t('dealer.contact_email')}`}
              value={search}
              onChange={(e) => setSearch(e.currentTarget.value)}
              leftSection={<IconSearch size={14} />}
              w={360}
            />
            <Button leftSection={<IconPlus size={16} />} onClick={() => setOpened(true)}>
              {t('dealer.create')}
            </Button>
          </Group>
          <OverviewPanel searchTerm={search} refreshKey={refreshKey} />
        </Tabs.Panel>

        <Tabs.Panel value="lookup" pt="md">
          <LookupPanel />
        </Tabs.Panel>
      </Tabs>

      <Modal
        opened={opened}
        onClose={() => {
          setOpened(false);
          resetForm();
        }}
        title={t('dealer.create')}
        size="lg"
      >
        <Stack>
          <TextInput
            label={t('dealer.code')}
            placeholder="e.g. KG0001"
            value={form.code}
            onChange={(e) => setForm({ ...form, code: e.currentTarget.value.toUpperCase() })}
            required
          />
          <TextInput
            label={t('dealer.name')}
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.currentTarget.value })}
            required
          />
          <TextInput
            label={t('dealer.contact_email')}
            type="email"
            value={form.contact_email}
            onChange={(e) => setForm({ ...form, contact_email: e.currentTarget.value })}
            required
          />
          <TextInput
            label={t('dealer.contact_phone')}
            value={form.contact_phone || ''}
            onChange={(e) => setForm({ ...form, contact_phone: e.currentTarget.value })}
          />
          <NumberInput
            label={t('dealer.incentive_rate')}
            decimalScale={4}
            step={0.05}
            min={0.01}
            max={0.99}
            value={Number(form.incentive_rate || '0.3000')}
            onChange={(v) => setForm({ ...form, incentive_rate: String(v) })}
          />

          <Select
            label={t('dealer.search_admin_user')}
            placeholder={t('dealer.search_admin_user_placeholder') as string}
            data={selectData}
            value={selectedUserId}
            onChange={(v) => setSelectedUserId(v)}
            searchable
            searchValue={userQuery}
            onSearchChange={setUserQuery}
            nothingFoundMessage={t('dealer.no_dealers') as string}
            clearable
            required
          />

          <Group justify="flex-end" mt="md">
            <Button variant="default" onClick={() => setOpened(false)}>
              {t('coupon.cancel')}
            </Button>
            <Button onClick={submit} disabled={!selectedUserId}>
              {t('dealer.create')}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* 测试用全局配置 Modal */}
      <Modal opened={settingsOpen} onClose={() => setSettingsOpen(false)} title={t('dealer.test_settings')} size="md">
        <Stack>
          <Text size="sm" c="dimmed">
            {t('dealer.test_settings_hint')}
          </Text>
          <NumberInput
            label={t('dealer.trial_offset_label')}
            description={t('dealer.trial_offset_description')}
            placeholder={t('dealer.trial_offset_placeholder') as string}
            min={0}
            value={trialOffsetSeconds}
            onChange={(v) => setTrialOffsetSeconds(v === '' ? '' : Number.isFinite(Number(v)) ? Number(v) : '')}
          />
          <Text size="xs" c="dimmed">
            {t('dealer.trial_offset_no_trial')}
          </Text>
          <Group justify="space-between" mt="md">
            <Button variant="subtle" color="gray" onClick={() => setTrialOffsetSeconds('')}>
              {t('dealer.trial_offset_clear')}
            </Button>
            <Group gap="xs">
              <Button variant="default" onClick={() => setSettingsOpen(false)}>
                {t('coupon.cancel')}
              </Button>
              <Button onClick={saveSettings} loading={savingSettings}>
                {t('dealer.save')}
              </Button>
            </Group>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
};

export default DealersListPage;
